import React, { useState, useEffect } from 'react';
import {
    PlusIcon,
    MagnifyingGlassIcon,
    PencilIcon,
    TrashIcon,
    EyeIcon,
    FunnelIcon,
    UserIcon,
    EnvelopeIcon,
    PhoneIcon,
    CalendarIcon,
    ArrowDownTrayIcon,
    ArrowUpTrayIcon,
    DocumentArrowDownIcon,
    ChevronLeftIcon,
    ChevronRightIcon
} from '@heroicons/react/24/outline';
import { apiUrl } from '../../utils/api';

interface User {
    _id: string;
    email: string;
    nombre: string;
    apellido: string;
    identificacion: string;
    telefono?: string;
    tipoSocio: 'propietario' | 'socio' | 'inversor' | 'administrador';
    fechaRegistro: string;
    activo: boolean;
}

interface UserFormData {
    email: string;
    password: string;
    nombre: string;
    apellido: string;
    identificacion: string;
    telefono: string;
    tipoSocio: 'propietario' | 'socio' | 'inversor' | 'administrador';
}

const UserManagement: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState<string>('');
    const [showModal, setShowModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [importFile, setImportFile] = useState<File | null>(null);
    const [importResults, setImportResults] = useState<any>(null);
    const [formData, setFormData] = useState<UserFormData>({
        email: '',
        password: '',
        nombre: '',
        apellido: '',
        identificacion: '',
        telefono: '',
        tipoSocio: 'propietario'
    });

    const [pagination, setPagination] = useState({
        current: 1,
        pages: 1,
        total: 0
    });

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchUsers(1);
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [filterType, searchTerm]);

    const fetchUsers = async (page = 1) => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            params.append('page', page.toString());
            params.append('limit', '10');
            if (filterType) params.append('tipoSocio', filterType);
            if (searchTerm) params.append('search', searchTerm);

            const response = await fetch(apiUrl(`/api/admin/users?${params}`), {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setUsers(data.data.users);
                if (data.data.pagination) {
                    setPagination(data.data.pagination);
                }
            } else {
                console.error('Error al obtener usuarios');
            }
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            const token = localStorage.getItem('token');
            const url = editingUser ? apiUrl(`/api/admin/users/${editingUser._id}`) : apiUrl('/api/admin/users');
            const method = editingUser ? 'PUT' : 'POST';

            const payload: Record<string, unknown> = { ...formData };
            if (editingUser && (!formData.password || !formData.password.trim())) {
                delete payload.password;
            }

            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                await fetchUsers(pagination.current);
                setShowModal(false);
                setEditingUser(null);
                setFormData({
                    email: '',
                    password: '',
                    nombre: '',
                    apellido: '',
                    identificacion: '',
                    telefono: '',
                    tipoSocio: 'propietario'
                });
            } else {
                const error = await response.json();
                alert(error.message || 'Error al guardar usuario');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error al guardar usuario');
        }
    };

    const handleEdit = (user: User) => {
        setEditingUser(user);
        setFormData({
            email: user.email,
            password: '',
            nombre: user.nombre,
            apellido: user.apellido,
            identificacion: user.identificacion,
            telefono: user.telefono || '',
            tipoSocio: user.tipoSocio
        });
        setShowModal(true);
    };

    const handleDelete = async (userId: string) => {
        if (!window.confirm('¿Estás seguro de que quieres desactivar este usuario?')) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(apiUrl(`/api/admin/users/${userId}`), {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                await fetchUsers(pagination.current);
            } else {
                alert('Error al desactivar usuario');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error al desactivar usuario');
        }
    };

    const getTipoSocioLabel = (tipo: string) => {
        const labels: { [key: string]: string } = {
            'propietario': 'Propietario',
            'socio': 'Socio',
            'inversor': 'Inversor',
            'administrador': 'Administrador'
        };
        return labels[tipo] || tipo;
    };

    const getTipoSocioColor = (tipo: string) => {
        const colors: { [key: string]: string } = {
            'propietario': 'bg-blue-100 text-blue-800',
            'socio': 'bg-green-100 text-green-800',
            'inversor': 'bg-purple-100 text-purple-800',
            'administrador': 'bg-red-100 text-red-800'
        };
        return colors[tipo] || 'bg-gray-100 text-gray-800';
    };

    const handleExportUsers = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(apiUrl('/api/excel/export/users'), {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `usuarios_${new Date().toISOString().split('T')[0]}.xlsx`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            } else {
                alert('Error al exportar usuarios');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error al exportar usuarios');
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(apiUrl('/api/excel/template/users'), {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'plantilla_usuarios.xlsx';
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            } else {
                alert('Error al descargar plantilla');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error al descargar plantilla');
        }
    };

    const handleImportUsers = async () => {
        if (!importFile) {
            alert('Por favor selecciona un archivo');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const formData = new FormData();
            formData.append('file', importFile);

            const response = await fetch(apiUrl('/api/excel/import/users'), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            const result = await response.json();

            if (response.ok) {
                setImportResults(result.data);
                await fetchUsers(1);
                setImportFile(null);
            } else {
                alert(result.message || 'Error al importar usuarios');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error al importar usuarios');
        }
    };

    // Client-side filtering removed in favor of server-side search
    const filteredUsers = users;

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                <div>
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2">Portal Inversionistas</h1>
                    <h2 className="text-2xl font-semibold text-gray-600">Gestión de Usuarios</h2>
                    <p className="text-gray-600 mt-2">Administra usuarios del sistema</p>
                </div>
                <div className="grid grid-cols-2 sm:flex sm:flex-row gap-3 sm:space-x-3">
                    <button
                        onClick={handleExportUsers}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center justify-center space-x-2"
                    >
                        <ArrowDownTrayIcon className="h-4 w-4" />
                        <span>Exportar</span>
                    </button>
                    <button
                        onClick={() => setShowImportModal(true)}
                        className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 flex items-center justify-center space-x-2"
                    >
                        <ArrowUpTrayIcon className="h-4 w-4" />
                        <span>Importar</span>
                    </button>
                    <button
                        onClick={handleDownloadTemplate}
                        className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 flex items-center justify-center space-x-2"
                    >
                        <DocumentArrowDownIcon className="h-4 w-4" />
                        <span>Plantilla</span>
                    </button>
                    <button
                        onClick={() => {
                            setEditingUser(null);
                            setFormData({
                                email: '',
                                password: '',
                                nombre: '',
                                apellido: '',
                                identificacion: '',
                                telefono: '',
                                tipoSocio: 'propietario'
                            });
                            setShowModal(true);
                        }}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2"
                    >
                        <PlusIcon className="h-4 w-4" />
                        <span>Nuevo Usuario</span>
                    </button>
                </div>
            </div>

            {/* Filtros y Búsqueda */}
            <div className="bg-white p-4 rounded-lg shadow-md border">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                        <div className="relative">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                            <input
                                type="text"
                                placeholder="Buscar por nombre, apellido, email o identificación..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>
                    <div className="flex items-center space-x-2">
                        <FunnelIcon className="h-4 w-4 text-gray-400" />
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">Todos los tipos</option>
                            <option value="propietario">Propietarios</option>
                            <option value="socio">Socios</option>
                            <option value="inversor">Inversores</option>
                            <option value="administrador">Administradores</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Tabla de Usuarios */}
            <div className="bg-white rounded-lg shadow-md border overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Usuario
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Identificación
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Tipo
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Contacto
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Estado
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Fecha Registro
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Acciones
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredUsers.map((user) => (
                                <tr key={user._id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="flex-shrink-0 h-10 w-10">
                                                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                                    <UserIcon className="h-5 w-5 text-blue-600" />
                                                </div>
                                            </div>
                                            <div className="ml-4">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {user.nombre} {user.apellido}
                                                </div>
                                                <div className="text-sm text-gray-500">{user.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <div className="font-mono bg-gray-100 px-2 py-1 rounded">
                                            {user.identificacion}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTipoSocioColor(user.tipoSocio)}`}>
                                            {getTipoSocioLabel(user.tipoSocio)}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {user.telefono ? (
                                            <div className="flex items-center space-x-1">
                                                <PhoneIcon className="h-4 w-4 text-gray-400" />
                                                <span>{user.telefono}</span>
                                            </div>
                                        ) : (
                                            <span className="text-gray-400">Sin teléfono</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${user.activo
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-red-100 text-red-800'
                                            }`}>
                                            {user.activo ? 'Activo' : 'Inactivo'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <div className="flex items-center space-x-1">
                                            <CalendarIcon className="h-4 w-4 text-gray-400" />
                                            <span>{new Date(user.fechaRegistro).toLocaleDateString()}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <div className="flex space-x-2">
                                            <button
                                                onClick={() => handleEdit(user)}
                                                className="text-blue-600 hover:text-blue-900"
                                            >
                                                <PencilIcon className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(user._id)}
                                                className="text-red-600 hover:text-red-900"
                                            >
                                                <TrashIcon className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Paginación */}
            <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 rounded-lg shadow-md">
                <div className="flex flex-1 justify-between sm:hidden">
                    <button
                        onClick={() => fetchUsers(pagination.current - 1)}
                        disabled={pagination.current === 1}
                        className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                    >
                        Anterior
                    </button>
                    <button
                        onClick={() => fetchUsers(pagination.current + 1)}
                        disabled={pagination.current === pagination.pages}
                        className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                    >
                        Siguiente
                    </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <div>
                        <p className="text-sm text-gray-700">
                            Mostrando página <span className="font-medium">{pagination.current}</span> de{' '}
                            <span className="font-medium">{pagination.pages}</span> ({pagination.total} resultados)
                        </p>
                    </div>
                    <div>
                        <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                            <button
                                onClick={() => fetchUsers(pagination.current - 1)}
                                disabled={pagination.current === 1}
                                className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:bg-gray-100"
                            >
                                <span className="sr-only">Anterior</span>
                                <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                            </button>

                            {/* Números de página */}
                            {[...Array(pagination.pages)].map((_, i) => {
                                const pageNumber = i + 1;
                                // Mostrar primera, última, actual y adyacentes
                                if (
                                    pageNumber === 1 ||
                                    pageNumber === pagination.pages ||
                                    (pageNumber >= pagination.current - 1 && pageNumber <= pagination.current + 1)
                                ) {
                                    return (
                                        <button
                                            key={pageNumber}
                                            onClick={() => fetchUsers(pageNumber)}
                                            className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${pageNumber === pagination.current
                                                ? 'z-10 bg-blue-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                                                : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                                                }`}
                                        >
                                            {pageNumber}
                                        </button>
                                    );
                                } else if (
                                    pageNumber === pagination.current - 2 ||
                                    pageNumber === pagination.current + 2
                                ) {
                                    return (
                                        <span key={pageNumber} className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 focus:outline-offset-0">
                                            ...
                                        </span>
                                    );
                                }
                                return null;
                            })}

                            <button
                                onClick={() => fetchUsers(pagination.current + 1)}
                                disabled={pagination.current === pagination.pages}
                                className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:bg-gray-100"
                            >
                                <span className="sr-only">Siguiente</span>
                                <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                            </button>
                        </nav>
                    </div>
                </div>
            </div>
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">
                            {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
                        </h2>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Email
                                </label>
                                <input
                                    type="email"
                                    required
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Contraseña {editingUser && '(dejar vacío para mantener la actual)'}
                                </label>
                                <input
                                    type="password"
                                    required={!editingUser}
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Nombre
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.nombre}
                                        onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Apellido
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.apellido}
                                        onChange={(e) => setFormData({ ...formData, apellido: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Número de Identificación
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={formData.identificacion}
                                    onChange={(e) => setFormData({ ...formData, identificacion: e.target.value })}
                                    placeholder="Ej: 12345678"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Teléfono
                                </label>
                                <input
                                    type="tel"
                                    value={formData.telefono}
                                    onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Tipo de Socio
                                </label>
                                <select
                                    required
                                    value={formData.tipoSocio}
                                    onChange={(e) => setFormData({ ...formData, tipoSocio: e.target.value as any })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="propietario">Propietario</option>
                                    <option value="socio">Socio</option>
                                    <option value="inversor">Inversor</option>
                                    <option value="administrador">Administrador</option>
                                </select>
                            </div>

                            <div className="flex justify-end space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                >
                                    {editingUser ? 'Actualizar' : 'Crear'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal de Importación */}
            {showImportModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <h2 className="text-xl font-bold mb-4">Importar Usuarios desde Excel</h2>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Seleccionar archivo Excel
                                </label>
                                <input
                                    type="file"
                                    accept=".xlsx,.xls"
                                    onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div className="bg-blue-50 p-3 rounded-lg">
                                <p className="text-sm text-blue-800">
                                    <strong>Nota:</strong> Descarga la plantilla para ver el formato correcto.
                                    Los usuarios se actualizarán por identificación si ya existen, o se crearán nuevos si no existen.
                                </p>
                                <p className="text-sm text-blue-800 mt-2">
                                    <strong>Contraseñas:</strong> Especifica una nueva contraseña en la columna "Contraseña".
                                    Si está vacía, no se cambiará la contraseña actual (usuarios existentes) o se usará "123456" (usuarios nuevos).
                                </p>
                            </div>

                            {importResults && (
                                <div className="bg-gray-50 p-3 rounded-lg">
                                    <h3 className="font-medium text-gray-900 mb-2">Resultados de la importación:</h3>
                                    <p className="text-sm text-gray-600">
                                        ✅ {importResults.success} usuarios procesados exitosamente
                                    </p>
                                    <p className="text-sm text-green-600">
                                        🆕 {importResults.created} usuarios creados
                                    </p>
                                    <p className="text-sm text-blue-600">
                                        🔄 {importResults.updated} usuarios actualizados
                                    </p>
                                    <p className="text-sm text-gray-600">
                                        📊 Total de filas procesadas: {importResults.total}
                                    </p>
                                    {importResults.errors.length > 0 && (
                                        <div className="mt-2">
                                            <p className="text-sm text-red-600 font-medium">Errores encontrados:</p>
                                            <ul className="text-xs text-red-600 mt-1 max-h-20 overflow-y-auto">
                                                {importResults.errors.slice(0, 5).map((error: any, index: number) => (
                                                    <li key={index}>• Fila {error.row}: {error.error}</li>
                                                ))}
                                                {importResults.errors.length > 5 && (
                                                    <li>... y {importResults.errors.length - 5} errores más</li>
                                                )}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                onClick={() => {
                                    setShowImportModal(false);
                                    setImportFile(null);
                                    setImportResults(null);
                                }}
                                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                            >
                                Cerrar
                            </button>
                            <button
                                onClick={handleImportUsers}
                                disabled={!importFile}
                                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                Importar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManagement;

